import { Help } from '@mui/icons-material'
import DeleteForever from '@mui/icons-material/DeleteForever'
import { Box, Grid, Modal, Paper } from '@mui/material'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { pallete } from '../../../theme'
import { Button } from './Button'
import { QRCode } from './QRCode'
import { TranslatableTypography } from './Typography'

const style = {
  modal: {
    wrapper: {
      padding: '20px',
      width: '80%',
      maxWidth: '60%',
      marginLeft: 'auto',
      marginRight: 'auto',
      marginTop: '15%',
      maxHeight: '70vh',
      overflowY: 'auto'
    },
    title: {
      fontWeight: 700,
      color: pallete.headerColor,
      marginBottom: '10px'
    },
    signature: {
      fontSize: '0.75rem',
      marginBottom: '15px'
    },
    qr: {
      paddingTop: '0px',
      paddingBottom: '8px',
      '& .companion-qr-code-image': {
        width: '170px',
        height: '170px',
        maxWidth: '45vw',
        maxHeight: '45vw'
      }
    },
    row: {
      paddingTop: '6px',
      paddingBottom: '6px'
    },
    label: {
      fontWeight: 700,
      color: pallete.headerColor
    },
    value: {
      wordBreak: 'break-word',
      fontSize: '0.75rem'
    },
    actionRow: {
      marginTop: '8px',
      marginBottom: '8px'
    },
    actionCol: {
      padding: '5px'
    },
    confirmText: {
      fontSize: '0.8rem',
      marginBottom: '8px',
      marginTop: '8px',
      textAlign: 'center',
      color: pallete.headerColor
    },
    confirmRow: {
      marginTop: '6px'
    },
    confirmButtonWrapper: {
      textAlign: 'center'
    }
  }
}

const readCachedConfigVersion = () => {
  try {
    const raw = localStorage.getItem('app-config-cache')
    if (!raw) {
      return undefined
    }

    const parsed = JSON.parse(raw)
    return parsed?.data?.version
  } catch (error) {
    return undefined
  }
}

export const AboutApplication = () => {
  const [open, setOpen] = useState(false)
  const [activePanel, setActivePanel] = useState(undefined)
  const loadedConfig = useSelector((state) => state?.config?.config)

  const versions = useMemo(() => {
    if (!open) {
      return undefined
    }

    return {
      cachedClientVersion: localStorage.getItem('app-build-version') ?? localStorage.getItem('cache-version'),
      loadedClientVersion: readCachedConfigVersion() ?? loadedConfig?.version,
      cachedServerVersion: localStorage.getItem('app-backend-version')
    }
  }, [open, loadedConfig])

  const resetApplication = () => {
    localStorage.clear()
    window.location.reload()
  }

  return (
    <Box>
      <Button
        icon={<Help />}
        variant='invert-contained'
        text='About application'
        onClick={() => { setOpen(true); setActivePanel(undefined) }}
      />
      <Modal open={open} onClose={() => setOpen(false)}>
        <Paper sx={style.modal.wrapper}>
          <TranslatableTypography sx={style.modal.title}>about</TranslatableTypography>
          <TranslatableTypography sx={style.modal.signature}>
            created-by
          </TranslatableTypography>

          <QRCode
            sx={style.modal.qr}
            labelSx={{ color: pallete.headerColor }}
          />

          <Grid container sx={style.modal.actionRow}>
            <Grid item xs={12} sx={style.modal.actionCol}>
              <Button
                variant='filled'
                sx={{ width: '100%' }}
                onClick={() => setActivePanel((value) => (value === 'versions' ? undefined : 'versions'))}
                text={activePanel === 'versions' ? 'Hide version info' : 'Reveal version info'}
              />
            </Grid>
            <Grid item xs={12} sx={style.modal.actionCol}>
              <Button
                variant='filled'
                sx={{ width: '100%' }}
                onClick={() => setActivePanel((value) => (value === 'reset' ? undefined : 'reset'))}
                icon={<DeleteForever />}
                text='Reset application cache'
              />
            </Grid>
          </Grid>

          {activePanel === 'versions' && (
            <Grid container>
              <Grid item xs={12} sx={style.modal.row}>
                <TranslatableTypography sx={style.modal.label}>Loaded client version:</TranslatableTypography>
                <TranslatableTypography sx={style.modal.value}>{versions?.cachedClientVersion ?? 'unknown'}</TranslatableTypography>
              </Grid>
              <Grid item xs={12} sx={style.modal.row}>
                <TranslatableTypography sx={style.modal.label}>Loaded config version:</TranslatableTypography>
                <TranslatableTypography sx={style.modal.value}>{versions?.loadedClientVersion ?? 'unknown'}</TranslatableTypography>
              </Grid>
              <Grid item xs={12} sx={style.modal.row}>
                <TranslatableTypography sx={style.modal.label}>Loaded server version:</TranslatableTypography>
                <TranslatableTypography sx={style.modal.value}>{versions?.cachedServerVersion ?? 'unknown'}</TranslatableTypography>
              </Grid>
            </Grid>
          )}

          {activePanel === 'reset' && (
            <Grid container>
              <Grid item xs={12}>
                <TranslatableTypography sx={style.modal.confirmText}>
                  Are you sure you want to reset application and reload data? This will also delete your locally cached favourite events. Log in before resetting to keep them safe — they will be restored from the server on next login.
                </TranslatableTypography>
              </Grid>
              <Grid item xs={6} sx={style.modal.confirmButtonWrapper}>
                <Button variant='filled' onClick={resetApplication} icon={<DeleteForever />} text='Yes' />
              </Grid>
              <Grid item xs={6} sx={style.modal.confirmButtonWrapper}>
                <Button variant='filled' onClick={() => setActivePanel(undefined)} text='No' />
              </Grid>
            </Grid>
          )}
        </Paper>
      </Modal>
    </Box>
  )
}
